/**
 * /superadmin — TOTP gate + main layout shell.
 *
 * Order of mount:
 *   1. Hit /api/superadmin/me → if 404 redirect to /home silently
 *   2. If totp_bypass=true OR totp_enabled=false (first-time setup) skip gate
 *   3. Otherwise render TotpGate; once verified render <SuperadminLayout/>
 *
 * 30-min inactivity auto-logout; heartbeat every 60s; gold impersonation banner
 * comes from a separate component (Session 2).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Bell, LogOut, RefreshCw, Menu, X,
  LayoutDashboard, AlertTriangle, Users, UserPlus, UserCheck, UserX, LineChart as LineChartIcon,
  IndianRupee, ArrowLeftRight, Wallet, TrendingUp,
  Stethoscope, ListChecks, Brain,
  Dumbbell, BookOpen, Apple, Calendar, MessageSquare,
  ShoppingBag, ClipboardList, Award, Flag,
  Activity, Banknote, AlertCircle, BellRing,
  Megaphone, History,
  ToggleRight, BadgeIndianRupee, UserCog, FileSearch,
} from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const SuperadminCtx = createContext(null);
export const useSuperadmin = () => useContext(SuperadminCtx);

const HEARTBEAT_MS = 60_000;
const INACTIVITY_MS = 30 * 60 * 1000;

// ─────────────────────────────────────────────────────────────
// Outer guard: load /me, gate by TOTP, render layout
// ─────────────────────────────────────────────────────────────
export default function SuperadminApp() {
  const navigate = useNavigate();
  const [bootState, setBootState] = useState("loading"); // loading | denied | totp | ready
  const [me, setMe] = useState(null);
  const lastActiveRef = useRef(Date.now());

  const checkAccess = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/superadmin/me");
      setMe(data);
      if (data.totp_bypass || !data.totp_enabled) {
        // Either dev bypass OR first login (must run TOTP setup later) — Session 1
        // shows the dashboard immediately so we can iterate. The setup flow lives
        // under /superadmin/settings/2fa-setup (rendered when totp_enabled=false
        // and totp_bypass=false).
        setBootState("ready");
      } else {
        setBootState("totp");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 401) {
        // Silently redirect — we never reveal that the route exists
        navigate("/home", { replace: true });
        setBootState("denied");
        return;
      }
      toast.error("Could not load Super Admin");
      setBootState("denied");
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Activity tracking
  useEffect(() => {
    if (bootState !== "ready") return;
    const bump = () => (lastActiveRef.current = Date.now());
    ["mousemove", "keydown", "click", "scroll"].forEach((e) =>
      window.addEventListener(e, bump, { passive: true })
    );
    const tick = setInterval(() => {
      if (Date.now() - lastActiveRef.current > INACTIVITY_MS) {
        clearInterval(tick);
        toast("You have been logged out due to inactivity. Log in again to continue.");
        useAuthStore.getState().logout?.();
        navigate("/login", { replace: true });
      }
    }, 30_000);
    const heartbeat = setInterval(() => {
      apiClient.post("/superadmin/heartbeat").catch(() => {});
    }, HEARTBEAT_MS);
    return () => {
      ["mousemove", "keydown", "click", "scroll"].forEach((e) =>
        window.removeEventListener(e, bump)
      );
      clearInterval(tick);
      clearInterval(heartbeat);
    };
  }, [bootState, navigate]);

  if (bootState === "loading" || bootState === "denied") {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>
        <div className="text-sm" style={{ color: "var(--text-tertiary, #8A8884)" }}>Loading…</div>
      </div>
    );
  }

  if (bootState === "totp") {
    return <TotpGate onPass={() => setBootState("ready")} />;
  }

  return (
    <SuperadminCtx.Provider value={{ me, refreshMe: checkAccess }}>
      <SuperadminLayout />
    </SuperadminCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────
// TOTP gate (shown on every login when bypass=false and enabled=true)
// ─────────────────────────────────────────────────────────────
function TotpGate({ onPass }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await apiClient.post("/superadmin/2fa/verify", { code });
      onPass();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ background: "var(--bg-base, #ECEAE6)" }}>
      <div
        className="w-full max-w-sm p-6"
        style={{
          background: "var(--bg-surface, #E4E2DE)",
          border: "1px solid var(--border-strong, rgba(12,12,11,0.18))",
          borderRadius: 20,
        }}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} style={{ color: "var(--brand-gold, #F5C518)" }} />
          <h1 className="font-barlow-cond" style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary, #0C0C0B)" }}>
            Super Admin · 2FA
          </h1>
        </div>
        <p className="mt-1 text-[13px]" style={{ color: "var(--text-secondary, #4A4845)" }}>
          Enter the 6-digit code from your authenticator app.
        </p>
        <input
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          pattern="\d*"
          placeholder="123 456"
          data-testid="superadmin-totp-input"
          className="mt-4 w-full text-center font-mono outline-none"
          style={{
            height: 56,
            fontSize: 24,
            letterSpacing: "0.4em",
            background: "var(--bg-inset, #D8D6D1)",
            border: "1px solid var(--border, rgba(12,12,11,0.09))",
            borderRadius: 12,
            color: "var(--text-primary, #0C0C0B)",
          }}
        />
        <button
          onClick={submit}
          disabled={busy || code.length !== 6}
          data-testid="superadmin-totp-submit"
          className="mt-4 w-full rounded-pill disabled:opacity-50"
          style={{
            background: "var(--accent, #2A2825)",
            color: "var(--bg-base, #ECEAE6)",
            height: 44,
            borderRadius: 100,
            fontFamily: "Barlow, sans-serif",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {busy ? "Verifying…" : "Continue"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Layout — fixed top bar + 220px sidebar + scrollable main
// ─────────────────────────────────────────────────────────────
function SuperadminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div
      className="superadmin-root"
      style={{
        minHeight: "100vh",
        background: "var(--bg-base, #ECEAE6)",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
        fontFeatureSettings: "'kern' 1, 'liga' 1",
        fontFamily: "Barlow, sans-serif",
        color: "var(--text-primary, #0C0C0B)",
      }}
      data-testid="superadmin-layout"
    >
      <SuperadminTopBar onMenuClick={() => setDrawerOpen(true)} />
      <div className="sa-shell" style={{ paddingTop: 56 }}>
        <SuperadminSidebar drawerOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
        {drawerOpen && (
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            data-testid="superadmin-drawer-backdrop"
            className="sa-drawer-backdrop"
          />
        )}
        <main className="sa-main">
          <Outlet />
        </main>
      </div>
      <style>{`
        .sa-shell { display: flex; }
        .sa-main {
          flex: 1; min-width: 0;
          min-height: calc(100vh - 56px);
          padding: 24px 28px 80px;
          overflow-x: hidden;
        }
        .sa-drawer-backdrop {
          display: none;
          position: fixed; inset: 56px 0 0 0;
          background: rgba(12,12,11,0.45);
          z-index: 45;
          border: none;
        }
        @media (max-width: 900px) {
          .sa-main { padding: 16px 14px 80px; }
          .sa-drawer-backdrop { display: block; }
        }
        /* Common responsive helpers for all super-admin pages */
        .superadmin-root table { font-size: 13px; }
        .superadmin-root [data-testid$="-table"] { min-width: 640px; }
        .superadmin-root .sa-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        @media (max-width: 640px) {
          /* Stack 2/3/4-col form grids to single column on small phones */
          .superadmin-root [style*="grid-template-columns: 1fr 1fr"],
          .superadmin-root [style*="grid-template-columns: repeat(2, 1fr)"],
          .superadmin-root [style*="grid-template-columns: 1fr 1fr 1fr"],
          .superadmin-root [style*="grid-template-columns: repeat(3, 1fr)"],
          .superadmin-root [style*="grid-template-columns: repeat(4, 1fr)"] {
            grid-template-columns: 1fr !important;
          }
          /* Drawer (user detail / pro detail) becomes full-screen on phone */
          .superadmin-root [data-testid$="-drawer"] {
            width: 100vw !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function SuperadminTopBar({ onMenuClick }) {
  const { me } = useSuperadmin();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [health, setHealth] = useState({ ok: true, label: "All systems operational" });

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        await apiClient.get("/health");
        setHealth({ ok: true, label: "All systems operational" });
      } catch {
        setHealth({ ok: false, label: "Issue detected" });
      }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, []);

  const istLabel = useMemo(() => {
    return now.toLocaleString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  }, [now]);

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 56,
        background: "var(--bg-elevated, #F0EEEA)",
        borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))",
        display: "flex",
        alignItems: "center",
        zIndex: 50,
      }}
      data-testid="superadmin-topbar"
    >
      <button
        onClick={onMenuClick}
        aria-label="Open menu"
        data-testid="superadmin-menu-btn"
        className="sa-menu-btn"
        style={{
          display: "none",
          width: 44,
          height: 44,
          marginLeft: 6,
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: "var(--text-primary, #0C0C0B)",
        }}
      >
        <Menu size={22} />
      </button>
      <div className="sa-brand" style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 20px", width: 220 }}>
        <ShieldCheck size={20} style={{ color: "var(--brand-gold, #F5C518)", flexShrink: 0 }} />
        <span style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 800, fontSize: 20, whiteSpace: "nowrap" }}>
          Super Admin
        </span>
      </div>
      <div className="sa-clock" style={{ flex: 1, textAlign: "center", fontSize: 13, fontWeight: 500, color: "var(--text-secondary, #4A4845)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
        {istLabel}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 16px" }}>
        <span className="sa-health" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <span
            aria-label={health.label}
            title={health.label}
            style={{
              width: 8, height: 8, borderRadius: "50%",
              background: health.ok ? "var(--success, #22C55E)" : "var(--error, #EF4444)",
            }}
          />
          <span className="sa-health-text" style={{ color: "var(--text-secondary, #4A4845)" }}>{health.label}</span>
        </span>
        <Bell size={16} className="sa-bell" style={{ color: "var(--text-secondary, #4A4845)" }} />
        <span className="sa-email" style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: 220 }}>{me?.email}</span>
        <button
          onClick={() => {
            useAuthStore.getState().logout?.();
            navigate("/login");
          }}
          data-testid="superadmin-logout"
          aria-label="Logout"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text-primary, #0C0C0B)",
            background: "transparent",
            border: "none",
          }}
        >
          <LogOut size={16} />
          <span className="sa-logout-text">Logout</span>
        </button>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .sa-menu-btn { display: inline-flex !important; }
          .sa-brand { width: auto !important; padding: 0 8px !important; }
          .sa-clock { display: none !important; }
          .sa-health-text { display: none !important; }
          .sa-bell { display: none !important; }
          .sa-email { max-width: 100px !important; font-size: 11px !important; }
          .sa-logout-text { display: none; }
        }
      `}</style>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Sidebar — grouped navigation
// ─────────────────────────────────────────────────────────────
const NAV = [
  {
    group: "Overview",
    items: [
      { to: "/superadmin", label: "Command Center", icon: LayoutDashboard, end: true },
      { to: "/superadmin/alerts", label: "Alerts", icon: AlertTriangle },
    ],
  },
  {
    group: "Users",
    items: [
      { to: "/superadmin/users", label: "All Users", icon: Users },
      { to: "/superadmin/users/active", label: "Active Today", icon: UserCheck },
      { to: "/superadmin/users/new", label: "New Registrations", icon: UserPlus },
      { to: "/superadmin/users/guests", label: "Guest Sessions", icon: UserX },
      { to: "/superadmin/users/retention", label: "Retention Analytics", icon: LineChartIcon },
    ],
  },
  {
    group: "Revenue",
    items: [
      { to: "/superadmin/revenue", label: "Overview", icon: IndianRupee },
      { to: "/superadmin/revenue/transactions", label: "Transactions", icon: ArrowLeftRight },
      { to: "/superadmin/revenue/payouts", label: "Payouts", icon: Wallet },
      { to: "/superadmin/revenue/mrr", label: "MRR and ARPU", icon: TrendingUp },
    ],
  },
  {
    group: "Professionals",
    items: [
      { to: "/superadmin/professionals", label: "All Professionals", icon: Stethoscope },
      { to: "/superadmin/professionals/pending", label: "Verification Queue", icon: ListChecks },
      { to: "/superadmin/professionals/ai-tasks", label: "AI Trainer Tasks", icon: Brain },
    ],
  },
  {
    group: "Content",
    items: [
      { to: "/superadmin/content/workouts", label: "Workout Manager", icon: Dumbbell },
      { to: "/superadmin/content/exercises", label: "Exercise Database", icon: BookOpen },
      { to: "/superadmin/content/food", label: "Food Database", icon: Apple },
      { to: "/superadmin/content/programmes", label: "Programme Manager", icon: Calendar },
      { to: "/superadmin/content/community", label: "Community Posts", icon: MessageSquare },
      { to: "/superadmin/content/reports", label: "Content Reports", icon: Flag },
    ],
  },
  {
    group: "Marketplace",
    items: [
      { to: "/superadmin/marketplace/products", label: "Products", icon: ShoppingBag },
      { to: "/superadmin/marketplace/orders", label: "Orders", icon: ClipboardList },
      { to: "/superadmin/marketplace/brands", label: "Brand Applications", icon: Award },
      { to: "/superadmin/marketplace/reports", label: "Reports", icon: Flag },
    ],
  },
  {
    group: "Platform",
    items: [
      { to: "/superadmin/platform/health", label: "API Health", icon: Activity },
      { to: "/superadmin/platform/scanner", label: "Scanner Diagnostics", icon: Activity },
      { to: "/superadmin/platform/costs", label: "Cost Monitor", icon: Banknote },
      { to: "/superadmin/platform/errors", label: "Error Logs", icon: AlertCircle },
      { to: "/superadmin/platform/notifications", label: "Notification Log", icon: BellRing },
    ],
  },
  {
    group: "Broadcast",
    items: [
      { to: "/superadmin/broadcast", label: "Compose", icon: Megaphone },
      { to: "/superadmin/broadcast/log", label: "Log", icon: History },
    ],
  },
  {
    group: "Settings",
    items: [
      { to: "/superadmin/settings/flags", label: "Feature Flags", icon: ToggleRight },
      { to: "/superadmin/settings/costs", label: "Cost Thresholds", icon: BadgeIndianRupee },
      { to: "/superadmin/settings/admins", label: "Admin Accounts", icon: UserCog },
      { to: "/superadmin/settings/audit", label: "Audit Log", icon: FileSearch },
      { to: "/superadmin/settings/diagnostics", label: "Config Diagnostics", icon: FileSearch },
    ],
  },
  {
    group: "Arena",
    items: [
      { to: "/superadmin/arena/applications", label: "Applications", icon: ListChecks },
      { to: "/superadmin/arena/gyms", label: "Gyms", icon: Dumbbell },
      { to: "/superadmin/arena/passes", label: "Passes", icon: Award },
      { to: "/superadmin/arena/checkins", label: "Check-ins", icon: Activity },
      { to: "/superadmin/arena/disputes", label: "Disputes", icon: Flag },
      { to: "/superadmin/arena/revenue", label: "Revenue", icon: IndianRupee },
    ],
  },
];

function SuperadminSidebar({ drawerOpen, onClose }) {
  return (
    <aside
      className="sa-sidebar"
      data-mobile-open={drawerOpen ? "1" : "0"}
      style={{
        width: 220,
        flexShrink: 0,
        background: "var(--bg-surface, #E4E2DE)",
        borderRight: "1px solid var(--border, rgba(12,12,11,0.09))",
        minHeight: "calc(100vh - 56px)",
        position: "sticky",
        top: 56,
        alignSelf: "flex-start",
        overflowY: "auto",
        maxHeight: "calc(100vh - 56px)",
      }}
      data-testid="superadmin-sidebar"
    >
      <button
        onClick={onClose}
        aria-label="Close menu"
        className="sa-sidebar-close"
        style={{
          display: "none",
          position: "absolute",
          top: 8,
          right: 8,
          width: 36, height: 36,
          alignItems: "center", justifyContent: "center",
          background: "transparent", border: "none",
          color: "var(--text-primary, #0C0C0B)",
        }}
      >
        <X size={20} />
      </button>
      {NAV.map((g) => (
        <div key={g.group} style={{ paddingBottom: 8 }}>
          <div
            style={{
              fontFamily: "Barlow, sans-serif",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "var(--text-tertiary, #8A8884)",
              padding: "16px 16px 6px",
            }}
          >
            {g.group}
          </div>
          {g.items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={onClose}
                data-testid={`nav-${it.to.replace(/\//g, "-")}`}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  height: 36,
                  padding: "0 16px",
                  margin: "0 8px",
                  borderRadius: 8,
                  fontFamily: "Barlow, sans-serif",
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 600,
                  color: isActive ? "var(--bg-base, #ECEAE6)" : "var(--text-secondary, #4A4845)",
                  background: isActive ? "var(--accent, #2A2825)" : "transparent",
                  textDecoration: "none",
                })}
              >
                <Icon size={14} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
      <style>{`
        @media (max-width: 900px) {
          .sa-sidebar {
            position: fixed !important;
            top: 56px !important;
            left: 0;
            bottom: 0;
            z-index: 50;
            width: 260px !important;
            transform: translateX(-100%);
            transition: transform 220ms ease;
            box-shadow: 4px 0 24px rgba(0,0,0,0.12);
            max-height: calc(100vh - 56px) !important;
          }
          .sa-sidebar[data-mobile-open="1"] {
            transform: translateX(0);
          }
          .sa-sidebar-close { display: inline-flex !important; }
        }
      `}</style>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// Section refresh helper
// ─────────────────────────────────────────────────────────────
export function SectionHeader({ title, lastUpdated, onRefresh }) {
  const ago = lastUpdated ? Math.floor((Date.now() - lastUpdated) / 1000) : null;
  return (
    <div className="flex items-end justify-between mb-3">
      <h2 style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 800, fontSize: 22 }}>
        {title}
      </h2>
      {onRefresh && (
        <div className="flex items-center gap-2 text-[12px]" style={{ color: "var(--text-tertiary, #8A8884)" }}>
          {ago != null && <span>Updated {ago}s ago</span>}
          <button
            onClick={onRefresh}
            data-testid={`refresh-${(title || "").toLowerCase().replace(/\W+/g, "-")}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
              color: "var(--text-primary, #0C0C0B)",
              padding: "0 10px",
              height: 28,
              borderRadius: 100,
              border: "1px solid var(--border-strong, rgba(12,12,11,0.18))",
              background: "transparent",
            }}
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      )}
    </div>
  );
}
